import { reactive } from 'vue';

// Small shared helpers.
//
// `$`/`$$` survive the move to Vue on purpose. Most of the app should never
// reach for them — that is what refs and templates are for — but the chassis
// still has to talk to things Vue does not own: the document element, a
// canvas handed to a share sheet, an element inside a WebView plugin.

export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];

export const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

export const uid = (n = 10) =>
  [...crypto.getRandomValues(new Uint8Array(n))]
    .map((b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36])
    .join('');

export function debounce(fn, ms = 150) {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), ms);
  };
}

/** Minimal pub/sub. Handlers never throw into the emitter. */
export function createBus() {
  const map = new Map();
  return {
    on(evt, fn) {
      (map.get(evt) ?? map.set(evt, new Set()).get(evt)).add(fn);
      return () => map.get(evt)?.delete(fn);
    },
    emit(evt, payload) {
      for (const fn of map.get(evt) ?? []) {
        try { fn(payload); } catch (e) { console.error(`bus:${evt}`, e); }
      }
    },
  };
}

/**
 * Transient status.
 *
 * A reactive list rather than a hidden div this function reaches into, so any
 * module can raise a toast without importing a component, and core/ui/Toast.vue
 * can render them without anything importing IT. The old version needed an
 * element with a specific id to already exist in index.html, which is exactly
 * the coupling the rewrite is meant to remove.
 */
export const toasts = reactive([]);

export function dismiss(id) {
  const i = toasts.findIndex((t) => t.id === id);
  if (i !== -1) toasts.splice(i, 1);
}

export function toast(text, { ms = 2200, kind = '', onClick = null } = {}) {
  const id = uid(8);
  toasts.push({ id, text, kind, onClick });
  // ms === 0 means it stays until something dismisses it — the update prompt.
  if (ms) setTimeout(() => dismiss(id), ms);
  return id;
}

/**
 * Haptics. iOS Safari has no navigator.vibrate at all, so on the web this is a
 * no-op there; the native build overrides it with the Taptic Engine, which is
 * why the app ends up with better haptics than the same page in a browser.
 */
let hapticsEnabled = true;
export function setHaptics(on) { hapticsEnabled = !!on; }
export function haptic(kind = 'tick') {
  if (!hapticsEnabled) return false;
  const ms = { tick: 8, pop: 14, undo: 6 }[kind] ?? 8;
  try { return navigator.vibrate?.(ms) ?? false; } catch { return false; }
}

export const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export function formatDuration(ms) {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
}
